import { Controller, SubmitHandler, useFormState } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { MessageCircleIcon } from "lucide-react";
import { generatePath, Link, useParams } from "react-router-dom";
import { ROUTES } from "@/routes_new";

import { patchOrganizationUser } from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import { getTimezones } from "@/api/endpoints/timezones";
import {
  CURRENCIES,
  CURRENCY,
  Currency,
  CURRENCY_CODE_TO_LABEL,
  LOCALE,
  Locale,
  LOCALE_TO_LABEL,
} from "@/constants";
import { EVENT } from "@/tracking_events";
import { i18n } from "@lingui/core";
import { useUser } from "@/hooks/useUser";
import { useOrganization } from "@/hooks/useOrganization";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import ErrorComponent from "@components/ui/error";
import { useIntercom } from "@/hooks/useIntercom";

const accountFormSchema = z.object({
  language: z.enum([LOCALE.SWEDISH, LOCALE.ENGLISH]),
  currency: z.enum([CURRENCY.SEK, CURRENCY.USD, CURRENCY.EUR, CURRENCY.NOK]),
  time_zone: z.string().min(1),
});

type AccountFormValues = z.infer<typeof accountFormSchema>;

export default function AccountForm() {
  const { t } = useLingui();
  const intercom = useIntercom();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const {
    data: userData,
    error: userError,
    refetch: refetchUser,
    isLoading: isUserLoading,
  } = useUser();

  const {
    data: orgData,
    error: orgError,
    isLoading: isOrgLoading,
  } = useOrganization();

  const {
    data: timezonesData,
    error: timezonesError,
    isLoading: isTimezonesLoading,
  } = useQuery({
    queryKey: ["timezones"],
    queryFn: getTimezones,
  });

  // Form state
  const form = useFormCompat<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    values:
      userData?.ok && orgData?.ok
        ? {
            language: userData.data.language,
            currency: userData.data.currency,
            time_zone: userData.data.time_zone,
          }
        : undefined,
  });

  const formState = useFormState({ control: form.control });

  // Mutation
  const { mutateAsync: mutateUpdateUser } = useMutation({
    mutationFn: (values: AccountFormValues) => {
      if (!userData?.ok || !orgData?.ok) {
        throw new Error();
      }
      return patchOrganizationUser({
        user_id: userData.data.id,
        organization_uuid: orgData.data.uuid,
        user: values,
      });
    },
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error();
      }

      postTrackingData(EVENT.UPDATE_SETTINGS);

      toast.success(t`Uppgifterna har sparats`);

      // Reload if language or currency changed to update UI
      if (
        userData?.ok &&
        (userData.data.currency !== response.data.currency ||
          userData.data.language !== response.data.language)
      ) {
        window.location.reload();
      }

      refetchUser();
    },
    onError: () => {
      toast.error(t`Kunde inte spara inställningarna`);
    },
  });

  // Handlers
  const handleSubmit: SubmitHandler<AccountFormValues> = (values) => {
    return mutateUpdateUser(values);
  };

  return (
    <Card className="p-0 gap-0">
      <CardHeader className="p-6 pb-4">
        <CardTitle className="text-xl -translate-y-0.5">
          <Trans>Kontoinställningar</Trans>
        </CardTitle>
      </CardHeader>

      {userData && orgData && timezonesData ? (
        userData.ok && orgData.ok && timezonesData.ok ? (
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <CardContent className="px-6 pb-6">
              <FieldGroup>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Controller
                    name="language"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = "account-language";
                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Språk</Trans>
                          </FieldLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id={id}
                              className="w-full"
                              aria-invalid={fieldState.invalid}
                            >
                              <SelectValue>
                                {(locale: Locale | null) =>
                                  locale
                                    ? i18n._(LOCALE_TO_LABEL[locale])
                                    : null
                                }
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="w-(--anchor-width)">
                              {Object.entries(LOCALE_TO_LABEL).map(
                                ([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {i18n._(label)}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="currency"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = "account-currency";
                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Valuta</Trans>
                          </FieldLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id={id}
                              className="w-full"
                              aria-invalid={fieldState.invalid}
                            >
                              <SelectValue>
                                {(currency: Currency | null) =>
                                  currency
                                    ? `${currency}, ${i18n._(CURRENCY_CODE_TO_LABEL[currency])}`
                                    : null
                                }
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="w-(--anchor-width)">
                              {CURRENCIES.map((currency) => (
                                <SelectItem key={currency} value={currency}>
                                  {currency},{" "}
                                  {i18n._(CURRENCY_CODE_TO_LABEL[currency])}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="time_zone"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = "account-timezone";
                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Tidszon</Trans>
                          </FieldLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id={id}
                              className="w-full"
                              aria-invalid={fieldState.invalid}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="w-(--anchor-width)">
                              {timezonesData.data.map((tz) => (
                                <SelectItem key={tz} value={tz}>
                                  {tz}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.error && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Field>
                    <FieldLabel htmlFor="account-password">
                      <Trans>Lösenord</Trans>
                    </FieldLabel>
                    <div className="flex gap-2">
                      <Input
                        id="account-password"
                        type="password"
                        value="****************"
                        readOnly
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        render={
                          <Link
                            to={generatePath(
                              ROUTES.SETTINGS_CHANGE_PASSWORD_ORGID,
                              { orgUuid }
                            )}
                          />
                        }
                        nativeButton={false}
                      >
                        <Trans>Uppdatera lösenord</Trans>
                      </Button>
                    </div>
                  </Field>
                </div>
              </FieldGroup>
            </CardContent>

            <CardFooter className="border-t pb-3 [.border-t]:pt-3 px-6">
              {intercom.show && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    intercom.show();
                  }}
                >
                  <Trans>Kontakta oss</Trans>
                  <MessageCircleIcon />
                </Button>
              )}
              <Button
                type="submit"
                disabled={formState.isSubmitting || !formState.isDirty}
              >
                <Trans>Spara</Trans>
                {formState.isSubmitting && <Spinner />}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <CardContent className="px-6 pb-6">
            <ErrorComponent
              heading={t`Åh nej!`}
              description={t`Något gick fel när vi försökte ladda in dina kontoinställningar.`}
            />
          </CardContent>
        )
      ) : isOrgLoading || isUserLoading || isTimezonesLoading ? (
        <CardContent className="px-6 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>

          <Skeleton className="h-14 w-full" />
        </CardContent>
      ) : userError || orgError || timezonesError ? (
        <CardContent className="px-6 pb-6">
          <ErrorComponent
            heading={t`Åh nej!`}
            description={t`Något gick fel när vi försökte ladda in dina kontoinställningar.`}
            error={userError || orgError || timezonesError}
          />
        </CardContent>
      ) : null}
    </Card>
  );
}
